import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { ZodError } from 'zod';

import { DashboardFilterValues, CalendarWeekData } from '../api/types';
import CalendarFilterBar from '../components/CalendarFilterBar';
import CalendarViewToggle from '../components/CalendarViewToggle';
import DayColumn from '../components/DayColumn';
import DayShiftCard from '../components/DayShiftCard';
import ShiftDetailPanel from '../components/ShiftDetailPanel';
import ShowFilter from '../components/ShowFilter';
import SummaryPills from '../components/SummaryPills';
import WeekNav from '../components/WeekNav';
import useCalendarWeek from '../hooks/useCalendarWeek';
import useDashboardFilters from '../hooks/useDashboardFilters';
import useCalendarNav from '../hooks/useCalendarNav';

import LoadingState from '@/components/LoadingState';

// Calendar tab: week nav + Today (left), summary pills + Show toggle (right),
// the approved horizontal filter bar beneath them (GRAVY-10305), and the
// 7-column day grid below.
//
// Filter state and the bar's revealed-ness are OWNED BY THE PAGE, not by this
// component (GRAVY-10321). They used to live here, and because the page mounts
// only the active tab, a glance at any action queue unmounted this component and
// silently discarded them. They arrive as props for the same reason the queues'
// filters do, and this tab feeds them straight into the calendar_week query key.

type Props = {
  agencyId: number;
  /** Owned by the page, so it survives tab switches (GRAVY-10321). */
  filters: DashboardFilterValues;
  onFiltersChange: (next: DashboardFilterValues) => void;
  /** Also owned by the page, for the same reason. */
  filtersOpen: boolean;
  onFiltersOpenChange: (next: boolean) => void;
};

// Week renders the seven-column grid; Day renders that single day's shifts as
// a list. Both are handed the SAME `days` payload — the server sends one entry
// for Day — so the two views cannot disagree about what is scheduled.
const DayOrWeek = ({
  view,
  days,
  onSelectShift,
}: {
  view: 'day' | 'week';
  days: NonNullable<CalendarWeekData['days']>;
  onSelectShift: (id: number) => void;
}) => {
  if (view === 'week') {
    return (
      <div className="dashv2-calendar">
        {days.map((day) => (
          <DayColumn key={day.date} day={day} onSelectShift={onSelectShift} />
        ))}
      </div>
    );
  }

  const shifts = days[0]?.shifts ?? [];
  if (shifts.length === 0) {
    // A day with nothing scheduled is a normal state, not a failure — say so
    // plainly rather than showing an empty frame the admin has to interpret.
    return (
      <div className="dashv2-dayempty" data-testid="dayViewEmpty">
        <p>No shifts scheduled for this day.</p>
      </div>
    );
  }

  return (
    <div className="dashv2-daylist" data-testid="dayViewList">
      {shifts.map((shift) => (
        <DayShiftCard key={shift.id} shift={shift} onOpen={onSelectShift} />
      ))}
    </div>
  );
};

const CalendarTab = ({
  agencyId,
  filters,
  onFiltersChange,
  filtersOpen,
  onFiltersOpenChange,
}: Props) => {
  const {
    view,
    setView,
    startDateISO,
    spanDays,
    rangeLabel,
    isOnToday,
    goPrev,
    goNext,
    goToday,
  } = useCalendarNav();
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useCalendarWeek(agencyId, startDateISO, filters, spanDays);
  const { data: filterOptions } = useDashboardFilters(agencyId);

  // Log the underlying cause so the failure is triageable rather than a silent
  // "Unable to load". A ZodError means the payload drifted from the contract;
  // anything else is an HTTP/request failure.
  useEffect(() => {
    if (!isError || !error) return;
    const kind = error instanceof ZodError ? 'contract' : 'request';
    // eslint-disable-next-line no-console
    console.error(`[DashboardV2] calendar_week ${kind} failure`, error);
    Sentry.captureException(error, {
      tags: { feature: 'dashboard_v2_calendar', failureKind: kind },
      extra: { agencyId, startDate: startDateISO },
    });
  }, [isError, error, agencyId, startDateISO]);

  const totals = data?.totals ?? { critical: 0, at_risk: 0, filled: 0 };

  return (
    <div>
      <div className="dashv2-controls">
        <div className="dashv2-navgroup">
          <WeekNav
            rangeLabel={rangeLabel}
            unit={view}
            onPrev={goPrev}
            onNext={goNext}
          />
          {/* Today jumps; Week and Day are the two spans (GRAVY-10329). */}
          <CalendarViewToggle
            view={view}
            onViewChange={setView}
            onToday={goToday}
            isOnToday={isOnToday}
          />
        </div>
        <div className="dashv2-summary">
          {/* The pills are the Calendar's fill-rate quick filter (GRAVY-10319).
              They read and write the SAME `fillStatus` the filter bar's Fill
              rate segment does — one canonical state, so the two cannot drift,
              Reset clears both, and a pill selection is part of the same
              calendar_week query key. */}
          <SummaryPills
            totals={totals}
            value={filters.fillStatus}
            onChange={(fillStatus) =>
              onFiltersChange({ ...filters, fillStatus })
            }
          />
          <ShowFilter
            open={filtersOpen}
            onToggle={() => onFiltersOpenChange(!filtersOpen)}
            value={filters}
          />
        </div>
      </div>

      {filtersOpen && (
        <div id="dashv2-calendar-filters">
          <CalendarFilterBar
            options={filterOptions}
            value={filters}
            onChange={onFiltersChange}
          />
        </div>
      )}

      {isLoading && !data ? (
        <LoadingState />
      ) : isError ? (
        <div className="dashv2-stub">
          <p>Unable to load the calendar.</p>
          <button
            type="button"
            className="dashv2-retry"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      ) : (
        <DayOrWeek
          view={view}
          days={data?.days ?? []}
          onSelectShift={setSelectedShiftId}
        />
      )}

      {selectedShiftId != null && (
        <ShiftDetailPanel
          agencyId={agencyId}
          shiftId={selectedShiftId}
          onClose={() => setSelectedShiftId(null)}
        />
      )}
    </div>
  );
};

export default CalendarTab;
